import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';
import { createContext } from 'react';

import { openPositionsBunja } from '@flipster/r230522/normal-sub/tables/private/positions';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber } from '@flipster/utils/num';

import { snapshotSpecBunja } from '../shared/snapshot/state';
import { createPositionOrDismissAtom } from '../utils/position-or-dismiss';

export const PositionCloseModalContext = createContext<OpenPosition | LegacyOpenPosition | null>(null);
export const PositionCloseModalScope = createScopeFromContext(PositionCloseModalContext);

export const MarketOnlyContext = createContext<boolean>(false);
export const MarketOnlyScope = createScopeFromContext(MarketOnlyContext);

export const positionCloseModalBunja = bunja(() => {
  const initPosition = bunja.use(PositionCloseModalScope);
  const marketOnly = bunja.use(MarketOnlyScope);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);

  const baseTabAtom = atom<'market' | 'limit'>('market');
  const tabAtom = atom((get) => (marketOnly ? 'market' : get(baseTabAtom)));

  const positionAtom = createPositionOrDismissAtom<OpenPosition>({
    openPositionsAtom,
    symbol: initPosition?.symbol,
    slot: initPosition?.slot,
  });

  const instrumentAtom = atom((get) => {
    const position = get(positionAtom);
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    const instrument = instrumentsKeyBySymbol?.[position.symbol];
    if (!instrument) throw new Error(`Instrument not found: ${position.symbol}`);
    return instrument;
  });

  const limitPriceAtom = atom<string>('0');
  const inputQtyAtom = atomWithDefault<string>((get) => {
    const position = get(positionAtom);
    if (!position) return '0';
    return Num(position.quantity).abs().toString();
  });

  const bboPriceAtom = atom((get) => {
    const position = get(positionAtom);
    if (!position) return '0';
    return (position.side === 'Long' ? position.bidPrice : position.askPrice).toString();
  });

  const percentAtom = atom((get) => {
    const position = get(positionAtom);
    const inputQty = get(inputQtyAtom);
    if (!position) return 0;
    return Number(
      Num(inputQty).dividedBy(Num(position.quantity).abs()).multipliedBy(100).toFixed(0, BigNumber.ROUND_HALF_UP),
    );
  });

  const amountAtom = atom((get) => {
    const tab = get(tabAtom);
    const limitPrice = get(limitPriceAtom);
    const bboPrice = get(bboPriceAtom);
    const price = Num(tab === 'limit' ? limitPrice : bboPrice);
    const inputQty = get(inputQtyAtom);
    const amount = Num(inputQty).multipliedBy(price);
    const instrument = get(instrumentAtom);
    const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);
    return amount.toFixed(quoteCurrencyPrecision);
  });

  const minQtyAtom = atom((get) => {
    const bboPrice = get(bboPriceAtom);
    const instrument = get(instrumentAtom);
    const minQty = Num(instrument.notionalMinOrderAmount)
      .dividedBy(bboPrice)
      .toFixed(instrument.tradeQtyPrecision, BigNumber.ROUND_UP);
    return minQty;
  });

  const isExceedLiqPriceAtom = atom((get) => {
    const tab = get(tabAtom);
    const position = get(positionAtom);
    const limitPrice = get(limitPriceAtom);
    if (tab === 'market') return false;
    return Boolean(
      position.liquidationPrice &&
        (position.side === 'Long'
          ? Num(limitPrice).isLessThan(position.liquidationPrice)
          : Num(limitPrice).isGreaterThan(position.liquidationPrice)),
    );
  });

  const isNotEnoughAtom = atom((get) => {
    const inputQty = get(inputQtyAtom);
    const minQty = get(minQtyAtom);
    return Num(inputQty).isLessThan(minQty);
  });

  // refer: https://prextech.slack.com/archives/C087D884VEE/p1762829688416739?thread_ts=1762824086.759899&cid=C087D884VEE
  const canCustomClosePositionAtom = atom((get) => {
    const position = get(positionAtom);
    const minQty = get(minQtyAtom);
    return Num(position.position).abs().isGreaterThanOrEqualTo(Num(minQty).multipliedBy(0.99));
  });

  const hasErrorAtom = atom((get) => {
    const canCustomClosePosition = get(canCustomClosePositionAtom);
    const tab = get(tabAtom);
    const isExceedLiqPrice = get(isExceedLiqPriceAtom);
    const isNotEnough = get(isNotEnoughAtom);

    if (!canCustomClosePosition) return false;

    if (tab === 'market') {
      return isNotEnough;
    }
    return isNotEnough || isExceedLiqPrice;
  });

  return {
    positionAtom,
    instrumentAtom,
    amountAtom,
    inputQtyAtom,
    baseTabAtom,
    tabAtom,
    marketOnly,
    limitPriceAtom,
    bboPriceAtom,
    percentAtom,
    isExceedLiqPriceAtom,
    isNotEnoughAtom,
    hasErrorAtom,
    canCustomClosePositionAtom,
  };
});
positionCloseModalBunja.debugLabel = 'positionCloseModalBunja';
